import React from 'react';
import { Box, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { separadorDeMiles } from '../../helpers';
import { SIGN_GREEN, usePaymentTeaser } from '../../branding';

/**
 * Cuadraditos de pago que acompañan a los totales (drawers de simple y multi).
 * La config vive en branding.ts (getPaymentTeaser): qué dice cada agencia,
 * si muestra efectivo, y si puede calcular el monto por cuota.
 */

interface Props {
	isExternal?: boolean;
	/** Total en USD sobre el que calcular el monto por cuota (si la agencia lo garantiza). */
	totalUSD?: number;
	/** Formatea un monto USD en la moneda principal del preview (COP en Colombia). */
	formatPrice?: (valueUSD: number) => string;
	mt?: number;
}

export const PaymentTeaser = ({ isExternal, totalUSD, formatPrice, mt = 1 }: Props): JSX.Element => {
	const teaser = usePaymentTeaser(isExternal);

	const format = formatPrice ?? ((valueUSD: number) => `USD ${separadorDeMiles(valueUSD)}`);
	const installmentAmountLine =
		teaser.installmentsCount && totalUSD && totalUSD > 0
			? `${teaser.installmentsCount} cuotas de ${format(Math.round(totalUSD / teaser.installmentsCount))}`
			: null;

	return (
		<Box display="grid" gridTemplateColumns={teaser.showCash ? '1fr 1fr' : '1fr'} gap={1} mt={mt}>
			<Box
				display="flex"
				gap={0.75}
				alignItems="flex-start"
				p={1}
				borderRadius="10px"
				bgcolor="rgba(49, 25, 75, 0.045)"
			>
				<CreditCardIcon color="secondary" sx={{ fontSize: 18, mt: 0.1 }} />
				<Box>
					<Typography variant="caption" color="primary" fontWeight={900} lineHeight={1.15} display="block">
						{teaser.installmentsTitle}
					</Typography>
					{installmentAmountLine && (
						<Typography variant="caption" color="secondary" fontWeight={900} lineHeight={1.2} display="block">
							{installmentAmountLine}
						</Typography>
					)}
					<Typography variant="caption" color="text.secondary" lineHeight={1.2} display="block">
						{teaser.installmentsNote}
					</Typography>
				</Box>
			</Box>
			{teaser.showCash && (
				<Box
					display="flex"
					gap={0.75}
					alignItems="flex-start"
					p={1}
					borderRadius="10px"
					bgcolor="rgba(46, 125, 50, 0.075)"
				>
					<AccountBalanceWalletIcon sx={{ fontSize: 18, mt: 0.1, color: SIGN_GREEN }} />
					<Box>
						<Typography variant="caption" color="primary" fontWeight={900} lineHeight={1.15} display="block">
							{teaser.cashTitle}
						</Typography>
						<Typography variant="caption" color="text.secondary" lineHeight={1.2} display="block">
							{teaser.cashNote}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default PaymentTeaser;
